import { Link, useNavigate } from "react-router-dom";
import { Info, Lock, Star } from "lucide-react";
import { useI18n } from "@/i18n";
import Sparkline, { DeltaChip } from "@/components/Sparkline";
import MediaImage from "@/components/MediaImage";

export function SignalBadge({ score, testid }) {
  const cls =
    score >= 80
      ? "border-[#00FF87]/80 bg-[#00FF87]/10 text-[#00FF87] shadow-[0_0_18px_rgba(0,255,135,0.4)]"
      : score >= 60
        ? "border-[#FFD600]/80 bg-[#FFD600]/10 text-[#FFD600] shadow-[0_0_18px_rgba(255,214,0,0.3)]"
        : score > 0
          ? "border-[#00F0FF]/70 bg-[#00F0FF]/10 text-[#00F0FF]"
          : "border-[#1F293D] bg-[#0B0E14] text-slate-500";
  return (
    <span data-testid={testid} className={`rounded-md border px-2.5 py-1 font-mono2 text-xs font-bold ${cls}`}>
      {score === null || score === undefined ? "—" : score}
    </span>
  );
}

export function playerLink(p) {
  return `/players/${p.player_id}`;
}

const STATE_STYLES = {
  called_up: "border-emerald-500/60 bg-emerald-500/20 text-emerald-300",
  debuted: "border-cyan-500/60 bg-cyan-500/20 text-cyan-300",
  started: "border-amber-500/60 bg-amber-500/20 text-amber-300",
  scored: "border-purple-500/60 bg-purple-500/20 text-purple-300",
  none: "border-[#1F293D] bg-[#0B0E14] text-slate-500",
};

const WHY_KEYS = new Set(["called_up_not_played", "played_not_started", "senior_history_unseen", "milestone_started", "milestone_scored", "minutes_trend", "age_bonus", "media_buzz", "rumored_play", "club_spotlight", "card_exists"]);

export default function PlayerCard({ p, inList, onToggleList }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const why = (p.top_reasons || []).filter((r) => WHY_KEYS.has(r.key)).slice(0, 2);
  return (
    <Link
      to={playerLink(p)}
      data-testid="player-card-item"
      className="hud-card hud-card-hover group relative rounded-xl p-5"
    >
      <div className="flex items-start gap-3">
        <MediaImage
          src={p.club_logo}
          alt={t("media.logo_alt", { club: p.club_name || "—" })}
          kind="club" size={56} surface="watchlist"
          imgClassName="object-contain p-1.5"
          testid={`player-media-${p.player_id}`}
        />
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-display text-xl font-semibold uppercase tracking-tight text-slate-50 group-hover:text-[#00FF87]">
            {p.name}
          </h3>
          {p.name_complete === false && (
            <p className="truncate font-mono2 text-[9px] uppercase tracking-wider text-[#FFD600]/80" data-testid={`player-name-incomplete-${p.player_id}`}>
              {t("player.name_incomplete")}
            </p>
          )}
          <p className="flex items-center gap-1.5 truncate text-xs text-slate-400">
            {p.position_group ? t(`pos.${p.position_group}`) : p.position || "—"} · {p.club_name || "—"}{p.parent_club && p.parent_club !== p.club_name ? ` (${t("player.loan_from", { club: p.parent_club })})` : ""}
          </p>
          <p className="mt-0.5 truncate font-mono2 text-[10px] uppercase tracking-wider text-slate-500">
            {p.league_name} · {p.age != null ? `${p.age}y` : "—"}
          </p>
        </div>
        <span className="flex shrink-0 items-center gap-3">
          <SignalBadge score={p.signal_score ?? null} testid={`player-signal-score-badge-${p.player_id}`} />
          <button
            data-testid={`hype-info-${p.player_id}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate("/how-it-works"); }}
            aria-label={t("hype.link")}
            title={t("hype.link")}
            className="p-1 text-slate-500 transition-colors hover:text-[#00F0FF]"
          >
            <Info className="h-3.5 w-3.5" />
          </button>
          <button
            data-testid={`player-list-star-${p.player_id}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleList(p); }}
            aria-pressed={inList}
            aria-label={inList ? t("player.remove_hint") : t("player.add_to_list")}
            title={inList ? t("player.remove_hint") : t("player.add_to_list")}
            className="rounded-md p-1 hover:bg-white/5"
          >
            <Star className={`h-4 w-4 ${inList ? "fill-[#FFD600] text-[#FFD600]" : "text-slate-600 hover:text-[#FFD600]"}`} />
          </button>
        </span>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <span className={`rounded-full border px-2 py-0.5 font-mono2 text-[10px] font-bold uppercase tracking-wider ${STATE_STYLES[p.milestone_state] || STATE_STYLES.none}`}>
          {t(`state.${p.milestone_state || "none"}`)}
        </span>
        <span className="flex items-center gap-1.5">
          {p.trend_locked ? (
            <Lock data-testid={`trend-locked-${p.player_id}`} className="h-3.5 w-3.5 text-[#FFD600]/70" />
          ) : (
            <>
              <DeltaChip delta={p.signal_delta_7d} testid={`player-delta7d-${p.player_id}`} />
              <Sparkline points={p.signal_spark} testid={`player-sparkline-${p.player_id}`} />
            </>
          )}
          <span className="font-mono2 text-[10px] text-slate-500">
            {p.appearances != null ? `${p.appearances} app` : ""}
          </span>
        </span>
      </div>
      {why.length > 0 && (
        <p className="mt-2 border-t border-[#1F293D]/60 pt-2 font-mono2 text-[10px] leading-snug text-slate-400" data-testid={`player-top-reasons-${p.player_id}`}>
          {why.map((r) => t(`reasons_short.${r.key}`, r.params || {})).join(" · ")}
        </p>
      )}
    </Link>
  );
}
